import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ПОРЫВ ВЕТРА НАД ЛУГОМ — эффект зеленых подложек (green_1..green_4).
 *
 * Раз в полминуты по лугу проходит порыв: по траве одна за другой катятся
 * травяные волны, а над ними ветер несет горсть листьев. Порыв идет тем же
 * ветром, что и облака (см. utils/effects/biome/windGust).
 *
 * ТРАВЯНАЯ ВОЛНА — как на лугу с высоты: ветер кладет траву, и поперек ветра
 * ложится светлая полоса (стебли повернулись к свету), а сразу за ней — полоса
 * чуть темнее (стебли в тени). Гребень волны — выгнутая дуга с неровным краем,
 * собранная из тонких светлых стеблей; вдоль гребня трава переливается: одни
 * места светлеют, другие гаснут. Прежняя рябь — прямые светлые штрихи вдоль
 * ветра — на траву под ветром похожа не была (замечание владельца игры).
 *
 * Волны лежат на самой траве, под дымкой пустых ячеек и под жетонами, и
 * остаются спокойным украшением: поле под порывом читается как прежде.
 *
 * Листья — бурые, ржавые, соломенные и зеленые. Зеленые добавлены по просьбе
 * владельца игры: свежий лист ярче и светлее травы, темный — темнее ее,
 * поэтому на лугу видны и те и другие.
 */
export const WIND_GUST_KIND = 'windGust';

const WIND_GUST = Object.freeze({
  // Первый порыв — вскоре после начала, следующий — через паузу от начала
  // предыдущего: двух порывов разом не бывает
  firstDelayMs: {min: 8000, max: 20000},
  intervalMs: {min: 26000, max: 48000},
  // Пятно порыва, в котором летят листья: поперек ветра и вдоль него,
  // размеров ячейки
  patchAcrossFactor: 6,
  patchAlongFactor: 3.5,
  // Скорость порыва, размеров ячейки в секунду
  speedFactor: {min: 1.6, max: 2.2},
  // Порыв начинается за столько ячеек до поля и уходит на столько же за него
  runwayFactor: 3,
  // За сколько порыв проступает в начале пути и тает в конце
  fadeMs: 1200,
  // Травяные волны: гребни идут по ветру вместе с порывом, один за другим
  waves: {
    // Гребней в порыве
    count: {min: 4, max: 6},
    // Расстояние между соседними гребнями вдоль ветра, размеров ячейки. Больше,
    // чем дуга гребня уходит назад своими концами: гребни не налезают друг на
    // друга
    gapFactor: {min: 2.4, max: 3.2},
    // Середины гребней разбросаны поперек ветра не дальше этого от оси порыва…
    laneFactor: 1.2,
    // …и с каждой новой жизнью гребень сдвигается вбок еще на столько же в
    // любую сторону: он проступает не там же, где растаял
    laneJitterFactor: 1,
    // Длина гребня поперек ветра, размеров ячейки
    lengthFactor: {min: 5, max: 7.5},
    // Гребень собран из прядей травы, уложенных внахлест: шаг между соседними
    // (размеров ячейки) и длина кадра пряди в шагах. К концам кадра прядь тает
    // так, что при кадре в два с небольшим шага соседние сливаются в ровную
    // полосу без «бусин»; из коротких прядей гребень гнется и меняет яркость
    // по длине. Шаг мельче — прядей и спрайтов больше: при этих числах в
    // гребне 6–8 прядей, в порыве — до 96 спрайтов волн и 27 листьев
    pieceStepFactor: 0.9,
    pieceOverlap: 2.2,
    // Высота кадра пряди (вдоль ветра), размеров ячейки; сама светлая полоса
    // занимает около половины кадра
    thicknessFactor: {min: 1.4, max: 2},
    // К концам гребень сужается до такой доли толщины…
    endThicknessShare: 0.55,
    // …и бледнеет: степень спада яркости (больше — концы длиннее и бледнее)
    endFadePower: 0.55,
    // Край гребня неровный: каждая прядь сдвинута вдоль ветра не дальше этого
    // (размеров ячейки) и толще или тоньше на такую долю
    pieceNudgeFactor: 0.12,
    pieceThicknessJitter: 0.3,
    // Гребень выгнут дугой: середина уходит вперед по ветру на такую долю его
    // длины. Дуга круче медленной волны: прямым гребень не бывает
    bowShare: {min: 0.12, max: 0.18},
    // Гребни одного порыва лежат поперек ветра с общим небольшим перекосом
    slantDeg: 6,
    // Медленная волна вдоль гребня — общая на весь порыв и лежит на земле:
    // гребни изгибаются согласно и остаются почти параллельны. Размах, длина
    // волны поперек ветра и вдоль него, размеров ячейки. Волна длинная и
    // пологая: дугу гребня она делает где круче, где положе, но не распрямляет
    // и не ломает
    wiggle: {
      amplitudeFactor: {min: 0.12, max: 0.2},
      lengthFactor: {min: 9, max: 13},
      runFactor: {min: 14, max: 22},
    },
    // Сила гребня: плотность пряди в самом ярком месте. Прядь — редкие тонкие
    // стебли и слабый отсвет под ними (втрое бледнее стеблей), светится поверх
    // травы; краска — цвет травы на свету, поэтому волна не белеет
    alpha: {min: 0.4, max: 0.54},
    // Гребень живет несколько секунд: проступает, катится по ветру и тает,
    // потом проступает снова чуть в стороне. Доля жизни на проступание и
    // столько же на таяние
    lifeMs: {min: 4500, max: 7000},
    fadeShare: 0.3,
    // Переливы: трава вдоль гребня светлеет пятнами. Пятна лежат на земле
    // (гребень, проходя, зажигает их) и медленно дышат
    shimmer: {
      // В тусклых местах от яркости гребня остается единица минус это
      depth: 0.65,
      // Размер пятен вдоль гребня и вдоль ветра (длины волн), размеров ячейки
      patchFactor: {min: 2, max: 3.2},
      runFactor: {min: 7, max: 10},
      // За столько пятно успевает разгореться и погаснуть
      periodMs: {min: 3500, max: 5000},
      // Пятно разгорается не вдруг: яркость растет, пока свет в этом месте
      // проходит от такой доли своего размаха до такой. Чем шире промежуток,
      // тем мягче края пятен и тем плавнее переливы
      rise: {from: 0.25, to: 0.75},
    },
    // Темная полоса сразу за светлой (с наветренной стороны): трава в тени.
    // Обычное наложение, не свечение: на светлой траве волну выдает она
    shade: {
      // На сколько тень отстает от светлой полосы, размеров ячейки
      offsetFactor: 0.5,
      // Тень — мягкое круглое пятно, растянутое вдоль гребня: чтобы соседние
      // слились в ровную полосу, кадр пятна длиннее шага втрое с лишним
      overlap: 3.2,
      // Плотность тени — доля плотности светлой полосы
      alphaShare: 0.4,
      // Кадр тени чуть выше кадра пряди
      thicknessShare: 1.15,
    },
  },
  // Листья над травой
  leaves: {
    // В полтора раза больше прежних 14–18: владелец игры просил добавить
    // листьев «процентов на 50»
    count: {min: 21, max: 27},
    lengthFactor: {min: 0.18, max: 0.24},
    // Прежние краски: темно-бурый, ржавый и бледно-соломенный
    tints: [0x7a3418, 0xb3361e, 0xf0e2a0],
    // Зеленые листья — в придачу к прежним: свежий, желто-зеленый и темный
    greenTints: [0x6fbf2a, 0xa6d43c, 0x1f5c1e],
    // Какая доля листьев порыва зеленая: зелень видна в каждом порыве, но
    // прежних красок — не меньше половины
    greenShare: {min: 0.35, max: 0.48},
    alpha: 0.9,
    // Лист летит чуть быстрее или медленнее порыва
    speedShare: {min: 0.9, max: 1.15},
    // Качается поперек ветра
    swayFactor: {min: 0.25, max: 0.4},
    swayPeriodMs: {min: 1400, max: 2200},
    spinDegPerSecond: {min: 180, max: 400},
    // Лист трепещет: поворачивается ребром столько раз в секунду
    flutterHz: {min: 2, max: 3},
    flutterDepth: 0.65,
  },
});

/** Листья порыва: те же летят с вуалями ветра над другой половиной лугов (./windVeils) */
export const WIND_GUST_LEAVES = WIND_GUST.leaves;

/**
 * У каждой подложки — свой открытый луг (plain, доли стороны картинки
 * пейзажа): волны катятся только по траве — на лесах, воде и скалах вокруг
 * луга их нет. И свои краски волны (waveTints), снятые с травы картинки:
 * light — трава на свету (ею светится светлая полоса), shade — трава в тени
 * (ею темнеет полоса за гребнем)
 */
export const WIND_GUST_EFFECT = Object.freeze({
  kind: WIND_GUST_KIND,
  backgrounds: Object.freeze({
    // Сочный луг среди холмов: трава оливковая
    [GROUND_BACKGROUNDS.GREEN_1]: {
      ...WIND_GUST, plain: {u: 0.5, v: 0.48, radiusU: 0.26}, waveTints: {light: 0xe6e08c, shade: 0x2f3018},
    },
    // Альпийский луг: трава светлее и холоднее
    [GROUND_BACKGROUNDS.GREEN_2]: {
      ...WIND_GUST, plain: {u: 0.5, v: 0.5, radiusU: 0.26}, waveTints: {light: 0xe2e89a, shade: 0x33381f},
    },
    // Золотая степь (green_3) и речная равнина (green_4) порыва не получают:
    // владелец игры отдал им вуали ветра (configs/biomeEffects/windVeils) —
    // половина лугов с волнами по траве, половина с вуалями
  }),
});
